import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Trash2, Download, FileText, Upload, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { sortDocuments } from '../sortUtils';

const DOC_TYPES = [
  { key: 'bylaws', label: 'Bylaws' },
  { key: 'articles', label: 'Articles of Incorporation' },
  { key: 'form_990', label: 'Form 990' },
  { key: 'audit', label: 'Audit Report' },
  { key: 'policy', label: 'Policy' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'conflict_of_interest', label: 'Conflict of Interest' },
  { key: 'board_packet', label: 'Board Packet' },
  { key: 'minutes_archive', label: 'Minutes Archive' },
  { key: 'other', label: 'Other' },
];

// 100 MB upload ceiling matches the backend BOARD_DOC_MAX_BYTES.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

const empty = () => ({ name: '', doc_type: 'other', file: null, notes: '', expires_at: '', version: '' });

function fmtBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(1)}MB`;
}

export default function DocumentsTab({ api, isAdmin, user, onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const q = filter ? `?doc_type=${filter}` : '';
      const { data } = await api.get(`/board/documents${q}`); setList(data);
    } catch (e) { /* noop */ }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(empty()); setProgress(0); setOpen(true); };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      toast.error(`File too large (${fmtBytes(f.size)}). Max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`);
      e.target.value = '';
      return;
    }
    // Store the raw File object — multipart upload streams it on save.
    setForm(prev => ({ ...prev, file: f, name: prev.name || f.name.replace(/\.[^.]+$/, '') }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Document name required'); return; }
    if (!form.file) { toast.error('Pick a file to upload'); return; }
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('name', form.name.trim());
      fd.append('doc_type', form.doc_type);
      if (form.notes) fd.append('notes', form.notes);
      if (form.expires_at) fd.append('expires_at', form.expires_at);
      if (form.version) fd.append('version', form.version);
      await api.post('/board/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 300000, // 5 min ceiling for very large files
      });
      toast.success('Document uploaded'); setOpen(false); load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
    setProgress(0);
  };

  const download = async (d) => {
    try {
      // Use the streaming proxy endpoint — works for both new (storage) and
      // legacy (inline base64) documents transparently.
      const resp = await api.get(`/board/documents/${d.id}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: d.mime || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.name + (d.name.includes('.') ? '' : ((d.mime || '').includes('pdf') ? '.pdf' : ''));
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Failed to download'); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await api.delete(`/board/documents/${d.id}`); toast.success('Deleted'); load(); onChange?.(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#2D1B4E]">Document Vault</h2>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="doc-filter">
            <option value="">All types</option>
            {DOC_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-1" /> Upload</Button>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No documents uploaded.</p>
      ) : (
        <Card className="bg-white border-[#DED8EB]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F4EF] text-[10px] uppercase tracking-wider text-[#8E85A0]">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Name</th>
                  <th className="text-left px-3 py-2 font-bold">Type</th>
                  <th className="text-left px-3 py-2 font-bold">Version</th>
                  <th className="text-left px-3 py-2 font-bold">Expires</th>
                  <th className="text-left px-3 py-2 font-bold">Size</th>
                  <th className="text-left px-3 py-2 font-bold">Uploaded</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortDocuments(list).map(d => {
                  const today = new Date().toISOString().slice(0, 10);
                  const in30 = (() => { const x = new Date(); x.setDate(x.getDate() + 30); return x.toISOString().slice(0, 10); })();
                  const type = DOC_TYPES.find(t => t.key === d.doc_type) || DOC_TYPES[DOC_TYPES.length - 1];
                  const expired = d.expires_at && d.expires_at < today;
                  const expiringSoon = !expired && d.expires_at && d.expires_at <= in30;
                  return (
                    <tr key={d.id}
                      className={`border-t border-[#F0EDF6] hover:bg-[#FAF7FF] ${expired ? 'bg-[#FDECEA]' : expiringSoon ? 'bg-[#FFF8E1]' : ''}`}
                      data-testid={`doc-row-${d.id}`}>
                      <td className="px-3 py-2 font-medium text-[#2D1B4E]">
                        {(expired || expiringSoon) && <AlertTriangle className={`h-3.5 w-3.5 inline mr-1 ${expired ? 'text-[#D9534F]' : 'text-[#FFB74D]'}`} />}
                        <FileText className="h-3.5 w-3.5 inline mr-1 text-[#6B3FA0]" />{d.name}
                      </td>
                      <td className="px-3 py-2"><Badge className="text-[10px] bg-[#EDE7F6] text-[#6B3FA0]">{type.label}</Badge></td>
                      <td className="px-3 py-2 text-[#5A4D6E]">{d.version || '—'}</td>
                      <td className={`px-3 py-2 ${expired ? 'text-[#D9534F] font-bold' : expiringSoon ? 'text-[#E65100] font-bold' : 'text-[#5A4D6E]'}`}>
                        {d.expires_at || '—'}{expired ? ' · EXPIRED' : expiringSoon ? ' · SOON' : ''}
                      </td>
                      <td className="px-3 py-2 text-[#5A4D6E] font-mono text-xs">{fmtBytes(d.size_bytes)}</td>
                      <td className="px-3 py-2 text-[#8E85A0] text-xs">{d.uploaded_by_name || '—'}<br /><span className="text-[10px]">{d.created_at?.slice(0, 10)}</span></td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="icon" onClick={() => download(d)} className="h-7 w-7" data-testid={`download-doc-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        {(isAdmin || d.uploaded_by === user?.id) && <Button variant="ghost" size="icon" onClick={() => del(d)} className="h-7 w-7 text-[#D9534F]" data-testid={`del-doc-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="2025 Form 990" data-testid="doc-name" /></div>
            <div><Label className="text-xs">Type</Label>
              <select value={form.doc_type} onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white" data-testid="doc-type">
                {DOC_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Version</Label><Input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="Rev 2" /></div>
              <div><Label className="text-xs">Expires</Label><Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs">File (PDF/DOCX/JPG/XLSX, max 100MB) *</Label>
              <div className="border-2 border-dashed border-[#DED8EB] rounded p-4 text-center cursor-pointer hover:border-[#9B7ED8]">
                <input type="file" onChange={handleFile} className="hidden" id="doc-file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.txt,.pptx,.ppt" data-testid="doc-file-input" />
                <label htmlFor="doc-file" className="cursor-pointer block">
                  <Upload className="h-6 w-6 mx-auto text-[#6B3FA0]" />
                  <p className="text-xs text-[#5A4D6E] mt-1">
                    {form.file ? `${form.file.name} · ${fmtBytes(form.file.size)}` : 'Click to choose file'}
                  </p>
                </label>
              </div>
              {busy && progress > 0 && (
                <div className="mt-2" data-testid="upload-progress">
                  <div className="h-1.5 w-full bg-[#EDE7F6] rounded-full overflow-hidden">
                    <div className="h-full bg-[#6B3FA0] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-[10px] text-[#5A4D6E] mt-1 text-right">{progress}% uploaded</p>
                </div>
              )}
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-doc-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{progress > 0 ? `Uploading ${progress}%…` : 'Uploading…'}</> : 'Upload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
